import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactModal from '../components/ContactModal';
import { Button } from '../components/ui/button';
import { CheckCircle, ArrowRight, Building2, Shield, FileCheck, Clock, Award, Users, DollarSign } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const BusinessFormationNew = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const pricingPlans = [
    { 
      name: 'LLC Formation', 
      price: 149, 
      features: ['Complete LLC Formation', 'Articles of Organization', 'Operating Agreement Template', 'EIN Registration', 'Basic Compliance Guide', 'Email Support'] 
    },
    { 
      name: 'S-Corp Formation', 
      price: 249,
      popular: true,
      features: ['S-Corporation Formation', 'Articles of Incorporation', 'Corporate Bylaws', 'Stock Certificates', 'EIN Registration', 'IRS Form 2553 Filing', 'Registered Agent Service (1 Year)', 'Priority Processing', 'Dedicated Support'] 
    },
    { 
      name: 'C-Corp Formation', 
      price: 349, 
      features: ['C-Corporation Formation', 'Articles of Incorporation', 'Corporate Bylaws', 'Stock Certificates', 'Minutes & Resolutions', 'EIN Registration', 'Registered Agent Service (1 Year)', 'Banking Resolution', 'Priority Processing', 'Dedicated Support', 'Business License Research'] 
    },
    { 
      name: 'Complete Package', 
      price: 499, 
      features: ['Any Business Structure', 'All Formation Documents', 'Operating Agreement/Bylaws', 'EIN Registration', 'Business License Research', 'Registered Agent (1 Year)', 'Banking Resolution', 'Stock Certificates/Membership Certificates', 'Corporate Kit', 'Trademark Search', 'Business Website Setup', '24/7 Priority Support', 'Legal Consultation (30 min)'] 
    }
  ];

  const faqs = [
    { q: 'What type of business structure should I choose?', a: 'The choice depends on your business goals, liability concerns, and tax preferences. LLCs offer flexibility and personal asset protection. S-Corps provide tax benefits for growing businesses. C-Corps are ideal for businesses seeking investors or planning to go public. We recommend consulting with our experts to determine the best fit.' },
    { q: 'How long does business formation take?', a: 'Most business formations are completed within 5-10 business days. With our expedited service, we can process your formation in as little as 24-48 hours. State filing times may vary.' },
    { q: 'What is an EIN and do I need one?', a: 'An EIN (Employer Identification Number) is like a social security number for your business. You need it to open a business bank account, hire employees, and file taxes. We include EIN registration in all our packages.' },
    { q: 'What is a Registered Agent?', a: 'A Registered Agent is a person or entity designated to receive legal documents on behalf of your business. Most states require one. Our packages include Registered Agent service for one year.' },
    { q: 'Do you handle state filing fees?', a: 'Our service fees cover our professional services. State filing fees are additional and vary by state, typically ranging from $50-$500.' }
  ];

  return (
    <div className="min-h-screen bg-navy-900">
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      
      {/* About the Service */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{backgroundImage: "url('https://images.unsplash.com/photo-1692133226337-55e513450a32?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMG1lZXRpbmclMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBtb2Rlcm4lMjBkYXJrfGVufDB8fHx8MTc3Mzk0Mjc0Nnww&ixlib=rb-4.1.0&q=85')"}}></div>
        <div className="absolute inset-0 banner-bg-navy"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center animate-fade-in">
            <h1 className="text-5xl font-bold text-gray-100 mb-6">Business Formation Services</h1>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Start your business the right way with professional business formation services. From LLCs to corporations, we handle all the paperwork and compliance requirements so you can focus on building your dream business.
            </p>
            <Button size="lg" onClick={() => setIsModalOpen(true)} className="bg-brand-gradient text-white hover:opacity-90 group animate-pulse-glow">
              Start Your Business Today
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* 3-Step Process */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Our Simple <span className="text-brand-primary">3-Step Process</span></h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              { icon: FileCheck, title: 'Choose Your Structure', desc: 'Select the business entity type that best fits your needs - LLC, S-Corp, or C-Corp.' },
              { icon: Building2, title: 'Submit Your Information', desc: 'Provide your business details through our simple online form. We handle all the paperwork.' },
              { icon: Shield, title: 'Get Legally Established', desc: 'We file with the state, obtain your EIN, and deliver all formation documents to you.' }
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div key={idx} className="text-center animate-scale-in bg-navy-800/50 rounded-xl p-6 hover:-translate-y-2 hover:bg-navy-700/80 hover:shadow-xl border border-transparent hover:border-gold-400/30 transition-all duration-300 cursor-default" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-brand-gradient w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse-glow">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-100 mb-3">{step.title}</h3>
                  <p className="text-base text-gray-300 leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why You Need It */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Why You Need <span className="text-brand-primary">Professional Business Formation</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              { title: 'Personal Asset Protection', desc: 'Protect your personal assets from business liabilities with proper entity formation.' },
              { title: 'Tax Advantages', desc: 'Choose the right structure to optimize your tax situation and save money.' },
              { title: 'Professional Credibility', desc: 'Establish your business as a legitimate, professional entity that customers trust.' },
              { title: 'Legal Compliance', desc: 'Ensure your business is properly registered and compliant with all state requirements.' },
              { title: 'Easier Financing', desc: 'Properly formed businesses have better access to loans, credit, and investment capital.' },
              { title: 'Business Growth', desc: 'Set up the right foundation for scaling your business and adding partners or investors.' }
            ].map((item, idx) => (
              <div key={idx} className="bg-navy-700 rounded-xl p-6 shadow-lg hover:shadow-2xl hover:-translate-y-2 hover:border-gold-400/50 border border-transparent transition-all duration-300 cursor-default animate-scale-in" style={{animationDelay: `${idx * 0.1}s`}}>
                <h3 className="text-xl font-bold text-gray-100 mb-3">{item.title}</h3>
                <p className="text-base text-gray-300">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 animate-fade-in">
            <h2 className="text-4xl font-bold text-gray-100 mb-4">Custom Company Formation Packages for Every Business</h2>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">Trademark Nexor offers a range of tailored packages designed for businesses of all sizes. Our affordable, all-inclusive options provide the support you need at every step of your company formation process.</p>
          </div>
          
          <div className="flex justify-center">
            <div className="max-w-xl w-full">
              <div className="rounded-3xl overflow-hidden shadow-2xl bg-navy-700 animate-scale-in">
                {/* Header */}
                <div className="bg-brand-gradient text-white text-center py-12 px-8">
                  <h3 className="text-2xl font-bold leading-tight">START YOUR BUSINESS<br />WITH EASE</h3>
                </div>
                
                {/* Content */}
                <div className="p-8 bg-navy-800">
                  <div className="space-y-6">
                    <div className="flex items-start space-x-4">
                      <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                      <p className="text-gray-200 font-medium leading-relaxed">
                        <strong>Make sure you're not liable for any business debts.</strong> Ensure you're not on the hook for business liabilities
                      </p>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                      <p className="text-gray-200 font-medium leading-relaxed">
                        <strong>Company Formation through a Simple Process:</strong> Our 3 step process discards with entering your details, Then selecting a service and lastly payment.
                      </p>
                    </div>
                    
                    <div className="flex items-start space-x-4">
                      <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                      <p className="text-gray-200 font-medium leading-relaxed">
                        <strong>Save Money on Taxes:</strong> For potential savings you get to decide whether you're are taxed as a LLC or a Corporation
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Footer Button */}
                <div className="p-8 bg-navy-700 text-center border-t border-navy-600">
                  <Button 
                    size="lg" 
                    onClick={() => setIsModalOpen(true)} 
                    className="bg-brand-gradient text-white hover:opacity-90 px-12 py-6 text-lg font-bold shadow-lg"
                  >
                    Get a Trademark
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-gray-100 mb-16 animate-fade-in">Why Choose <span className="text-brand-primary">Trademark Nexor</span></h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, title: 'Expert Guidance', desc: 'Business formation specialists guide you every step of the way' },
              { icon: Clock, title: 'Fast Processing', desc: 'Quick turnaround with expedited options available' },
              { icon: DollarSign, title: 'Affordable Pricing', desc: 'Competitive rates with transparent pricing - no hidden fees' },
              { icon: Users, title: 'Ongoing Support', desc: 'Dedicated support team available to answer your questions' }
            ].map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="text-center animate-scale-in bg-navy-800/50 rounded-xl p-6 hover:-translate-y-2 hover:bg-navy-700/80 hover:shadow-xl border border-transparent hover:border-gold-400/30 transition-all duration-300 cursor-default" style={{animationDelay: `${idx * 0.1}s`}}>
                  <div className="bg-navy-700 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <Icon className="text-brand-primary" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-100 mb-2">{item.title}</h3>
                  <p className="text-base text-gray-300">{item.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-navy-800">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <div className="animate-fade-in">
              <img src="https://images.unsplash.com/photo-1614184039972-78d7e40597e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxzdGFydHVwJTIwZW50cmVwcmVuZXVyJTIwZWNvbW1lcmNlJTIwYnVzaW5lc3MlMjBncm93dGh8ZW58MHx8fHwxNzczOTQwMDkwfDA&ixlib=rb-4.1.0&q=85&w=800" alt="Business Formation Benefits" className="rounded-2xl shadow-xl hover-zoom" />
            </div>
            <div className="animate-slide-up">
              <h2 className="text-4xl font-bold text-gray-100 mb-6">What's Included in Our Service</h2>
              <div className="space-y-4">
                {[
                  'Complete business name availability check',
                  'Preparation and filing of all formation documents',
                  'EIN (Employer Identification Number) registration',
                  'Registered Agent service for one year',
                  'Operating Agreement or Corporate Bylaws',
                  'Ongoing compliance guidance and support'
                ].map((benefit, idx) => (
                  <div key={idx} className="flex items-start space-x-3">
                    <CheckCircle className="text-brand-primary flex-shrink-0 mt-1" size={24} />
                    <p className="text-lg text-gray-200">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <TestimonialsSlider />

      {/* FAQs */}
      <section className="py-20 bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-gray-100 mb-12 animate-fade-in">Frequently Asked <span className="text-brand-primary">Questions</span></h2>
            <div className="bg-navy-700 rounded-2xl shadow-xl p-8">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((faq, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left font-semibold text-gray-100">{faq.q}</AccordionTrigger>
                    <AccordionContent className="text-base text-gray-300">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-brand-gradient">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center animate-fade-in">
            <h2 className="text-4xl font-bold text-white mb-6">Ready to Start Your Business?</h2>
            <p className="text-xl text-gray-200 mb-8">Get your business legally established today with our expert formation services</p>
            <Button size="lg" onClick={() => setIsModalOpen(true)} className="bg-navy-500 text-white hover:bg-navy-400 shadow-xl border-2 border-navy-400">
              Get Started Now
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default BusinessFormationNew;
